import { useRef, useEffect, useState } from 'react';
import { PanelRightClose, MessageSquare, Plus, Sparkles } from 'lucide-react';
import { useAIAgent } from '../context/AIAgentContext';
import { useAuth } from '../../contexts/AuthContext';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { FeedbackMessage } from './FeedbackMessage';
import { shouldShowFeedbackPrompt, submitAgentFeedback } from '../../services/feedbackService';
import { logger } from '../../lib/logger';

export function AISidebar() {
  const {
    isOpen,
    setIsOpen,
    messages,
    isLoading,
    sendMessage,
    startNewConversation,
  } = useAIAgent();
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showFeedbackMessage, setShowFeedbackMessage] = useState(false);
  const [feedbackChecked, setFeedbackChecked] = useState(false);

  // Scroll to bottom when messages change or feedback appears
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showFeedbackMessage]);

  // Check if we should show feedback prompt when sidebar opens
  useEffect(() => {
    if (!isOpen || !user?.id || feedbackChecked) return;

    const checkFeedback = async () => {
      try {
        const shouldShow = await shouldShowFeedbackPrompt(user.id);
        if (shouldShow) {
          // Small delay to not interrupt the user immediately
          setTimeout(() => setShowFeedbackMessage(true), 1500);
        }
      } catch (error) {
        logger.error('Error checking feedback prompt:', error);
      } finally {
        setFeedbackChecked(true);
      }
    };

    checkFeedback();
  }, [isOpen, user?.id, feedbackChecked]);

  const handleFeedbackSubmit = async (rating: number, comment?: string) => {
    if (!user?.id) return;
    await submitAgentFeedback(user.id, rating, comment);
    // Confirmation is shown inside FeedbackMessage component
  };

  const handleFeedbackDismiss = () => {
    setShowFeedbackMessage(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar */}
      <aside className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-primary-light">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">KI-Assistent</h2>
              <p className="text-xs text-gray-500">Wie kann ich dir helfen?</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-tour="ai-new"
              onClick={startNewConversation}
              className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
              title="Neue Unterhaltung"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Einklappen"
            >
              <PanelRightClose className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !showFeedbackMessage ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 bg-gradient-primary-soft rounded-2xl flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Willkommen beim KI-Assistenten
              </h3>
              <p className="text-sm text-gray-500 mb-6 max-w-[250px]">
                Ich kann dir bei verschiedenen Aufgaben helfen. Probiere es aus!
              </p>
              <div data-tour="ai-suggestions" className="space-y-2 w-full">
                <SuggestionButton
                  onClick={() => sendMessage('Erstelle einen neuen Lead für Maria Schmidt')}
                >
                  Lead erstellen
                </SuggestionButton>
                <SuggestionButton
                  onClick={() => sendMessage('Erstelle ein Todo: Kunden anrufen, fällig morgen')}
                >
                  Aufgabe erstellen
                </SuggestionButton>
                <SuggestionButton
                  onClick={() => sendMessage('Gehe zur Leads-Seite')}
                >
                  Navigieren
                </SuggestionButton>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}

              {/* Feedback Message - appears as a chat message from the AI */}
              {showFeedbackMessage && (
                <FeedbackMessage
                  onSubmit={handleFeedbackSubmit}
                  onDismiss={handleFeedbackDismiss}
                />
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <ChatInput onSend={sendMessage} isLoading={isLoading} />
      </aside>
    </>
  );
}

interface SuggestionButtonProps {
  onClick: () => void;
  children: React.ReactNode;
}

function SuggestionButton({ onClick, children }: SuggestionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-2.5 text-sm text-left text-gray-700 bg-gray-50 hover:bg-primary/10 hover:text-primary rounded-xl border border-gray-100 hover:border-primary/30 transition-colors"
    >
      {children}
    </button>
  );
}
